import { ConstantsService } from './../../../../services/constants-service';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Component, ElementRef, OnInit, OnChanges, ViewChild, Input, ChangeDetectorRef } from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import {
  EntityRelationsService, MetaForms,
  RelationshipPanelActions
} from 'app/entities/relations/entity-relations.service';
import { EntityIdDetails, EntityType } from 'app/shared/models/entity-id-details.model';
import { EntityRelation } from 'app/shared/models/entity-relation.model';

import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { FormErrors } from 'app/shared/validators/generic-validators';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { MonitoringStatusService } from 'app/shared/services/monitoring-status.service';

@Component({
  selector: 'add-relation',
  templateUrl: './add-relation.component.html',
  styleUrls: ['./add-relation.component.css']
})

export class AddRelationComponent implements OnInit, OnChanges {
  public entityIdDetails: EntityIdDetails;
  private addRelationForm: FormGroup;
  selectedRelation: EntityRelation;
  entityName: string;
  action: string;
  isLoading = false;
  authorizedTypes: ApplicationValue[];
  relationRoles: ApplicationValue[];
  selectedRelationType: string;
  selectedEntity: GenericSearchDetails;
  relationAddSearchURI = ConstantsService.ADD_RELATION_SEARCH;
  addRelationPlaceholder = MessageService.HEADER_SEARCH_PLACEHOLDER;
  flagSavingRelation = false;

  shareTagVisible = ['Shareholder', 'ContactPerson', 'General Partner', 'Limited Partner'];

  constructor(
    public dialogRef: MdDialogRef<any>,
    private formBuilder: FormBuilder,
    private snackBarService: SnackBarService,
    private warmUpService: WarmUpService,
    private entityRelationsService: EntityRelationsService,
    private cdr: ChangeDetectorRef,
    private monitoringStatus: MonitoringStatusService
  ) { }

  ngOnInit() {
    this.addRelationForm = this.formBuilder.group({
      name: ['', Validators.required],
      comment: [''],
      relation: ['', Validators.required],
      startService: [''],
      isUbo: [''],
      endService: [''],
      authorizedType: [''],
      legalShares: [''],
      economicShares: [''],
      voting: ['']
    });
    this.loadRelationTypes();
    this.loadAuthorizedType();

    if (this.action === 'Edit' && this.selectedRelation) {
      const metaData = this.selectedRelation.meta;
      const relationname = this.getSelectedRelationName(this.selectedRelation.role);
      this.selectedRelationType = relationname.name;
      this.isShareMeta();
      this.isDirectorMeta();
      this.addRelationForm.patchValue({
        name: this.selectedRelation.name,
        comment: metaData && metaData['OtherDetails'],
        relation: this.selectedRelation.role,
        startService: metaData && metaData['StartOfServices'],
        isUbo: this.selectedRelation.ubo,
        endService: metaData && metaData['EndOfServices'],
        authorizedType: metaData && metaData['AuthorizedType'],
        legalShares: metaData && metaData['Shares'],
        economicShares: metaData && metaData['EconomicShares'],
        voting: metaData && metaData['VotingRights'],
      });
    }
  }

  ngOnChanges() {
    this.loadRelationTypes();
    this.loadAuthorizedType();
  }

  relationTypeChange(relatoinId: number) {
    const selectedDocument = this.getSelectedRelationName(relatoinId);
    if (selectedDocument) {
      this.selectedRelationType = selectedDocument.name;
      const relationNme = this.addRelationForm.controls['name'].value;
      this.addRelationForm.reset();
      this.addRelationForm.controls['name'].setValue(relationNme);
    }
    this.cdr.detectChanges();
  }

  getSelectedRelationName(relatoinId: number) {
    return this.relationRoles.find(relation => relation.id === relatoinId);
  }

  isCommentMeta() {
    return this.entityRelationsService.getMetaFormType(this.selectedRelationType) === MetaForms.OTHER;
  }

  isShareMeta() {
    return this.entityRelationsService.getMetaFormType(this.selectedRelationType) === MetaForms.SHAREHOLDER;
  }

  isDirectorMeta() {
    return this.entityRelationsService.getMetaFormType(this.selectedRelationType) === MetaForms.DIRECTOR;
  }

  loadRelationTypes() {
    const relations = this.entityRelationsService.loadRelationTypes();
    if (relations instanceof Observable) {
      relations.subscribe(
        response => {
          this.setSortedRelationRoles(response);
        }
      );
    } else {
      this.setSortedRelationRoles(relations);
    }
  }

  loadAuthorizedType() {
    const authorizedList =
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.AUTHORIZED_TYPE);
    if (authorizedList instanceof Observable) {
      authorizedList.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.relationRoles = response.values;
        }
      );
    } else {
      this.authorizedTypes = authorizedList;
    }
  }

  onEntitySelection(searchDetails: GenericSearchDetails) {
    const monitoringStatus = this.monitoringStatus.checkSearchMonitoringStatus(searchDetails);
    if (monitoringStatus && !confirm(monitoringStatus)) {
      return;
    }
    this.selectedEntity = searchDetails;
    this.addRelationForm.controls['name'].setValue(this.selectedEntity.clientName);
    this.addRelationForm.controls['name'].markAsTouched();
  }

  onSubmit({ value, valid }): void {
    const relationVO = new EntityRelation();
    // Disable save button once it is clicked for saving data.
    this.flagSavingRelation = true;
    if (this.action === 'Edit') {
      relationVO.id = this.selectedRelation.id;
    }
    relationVO.role = value.relation;
    relationVO.ubo = value.isUbo;
    relationVO.name = value.name;
    if (this.isShareMeta()) {
      relationVO.meta['Shares'] = value.legalShares;
      relationVO.meta['EconomicShares'] = value.economicShares;
      relationVO.meta['VotingRights'] = value.voting;
    } else if (this.isDirectorMeta()) {
      relationVO.meta['StartOfServices'] = value.startService;
      relationVO.meta['EndOfServices'] = value.endService;
      relationVO.meta['AuthorizedType'] = value.authorizedType;
    } else {
      relationVO.meta['OtherDetails'] = value.comment;
    }
    if (this.action === 'Add') {
      relationVO.subjectId = this.entityIdDetails.entityId;
      relationVO.entityId = this.selectedEntity.entityId;
      relationVO.clientId = this.selectedEntity.clientId;
      relationVO.detailId = this.selectedEntity.id;
      relationVO.entityType = EntityType[this.entityIdDetails.entityType.toString()];
      this.entityRelationsService.addRelation(this.entityIdDetails.entityId, relationVO)
        .subscribe(response => {
          this.callSnackBarService(
            response,
            this.action === 'Add' && MessageService.ADD_RELATION_SUCCESS
            || MessageService.UPDATED_RELATION_SUCCESS);
          this.entityRelationsService.sendMessage(RelationshipPanelActions.UPDATE_RELATIONSHIPS.toString());
          this.dialogRef.close(response.arrResult);
          // Enable save button once data is saved and pop-up is closed.
          this.flagSavingRelation = false;
        }, err => {
          this.callSnackBarService(err, '');
          this.dialogRef.close();
        });
    } else {
      relationVO.subjectId = this.selectedRelation.subjectId;
      relationVO.entityId = this.selectedRelation.entityId;
      relationVO.clientId = this.selectedRelation.clientId;
      relationVO.detailId = this.selectedRelation.detailId;
      relationVO.entityType = EntityType[this.entityIdDetails.entityType.toString()];
      this.entityRelationsService.updateRelation(relationVO.id, relationVO)
        .subscribe(response => {
          this.callSnackBarService(
            response,
            this.action === 'Add' && MessageService.ADD_RELATION_SUCCESS
            || MessageService.UPDATED_RELATION_SUCCESS);
          this.entityRelationsService.sendMessage(RelationshipPanelActions.UPDATE_RELATIONSHIPS.toString());
          this.dialogRef.close(response);
          // Enable save button once data is saved and pop-up is closed.
          this.flagSavingRelation = false;
        }, err => {
          this.callSnackBarService(err, '');
          this.dialogRef.close();
        });

    }
  }

  callSnackBarService(response: any, successMsg) {
    this.snackBarService
      .showStatus(response,
      successMsg,
      MessageService.DOCUMENT_FAILURE);
  }

  private setSortedRelationRoles(relations: ApplicationValue[]) {
    const uniqueValues = new Set();
    this.relationRoles = relations.filter((elem, index, self) => {
      if (uniqueValues.has(elem.name)) {
        return false;
      }
      uniqueValues.add(elem.name);
      return true;
    }).sort((av1, av2) => {
      return (av1.name < av2.name) ? -1 : (av1.name > av2.name) ? 1 : 0;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/relations/relationship/addrelation/add-relation.component.ts